import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ParentAssetMatch } from '../api/client';
import type { BillableMeterV2, VirtualMeter, VirtualMeterSource } from '../api/types';
import { Card, Alert, Button } from '../components/ui';
import { ParentAssetPicker } from '../components/ParentAssetPicker';

// PR 2c — Virtual Meters management page.
//
// A virtual meter is a software-defined meter point computed by adding /
// subtracting fractions of physical meters' 30-min interval data. It appears
// in OpenRemote as a regular ElectricityConsumerAsset so existing tariff
// assignment + billing infrastructure works unchanged.

interface DraftSource {
  source_meter_asset_id: string;
  operation: '+' | '-';
  fraction: number;
}

export default function VirtualMeters() {
  const [virtualMeters, setVirtualMeters] = useState<VirtualMeter[]>([]);
  const [candidateSources, setCandidateSources] = useState<BillableMeterV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor state — inline create/edit form. id=null → create; id=string → edit.
  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draftSerial, setDraftSerial] = useState('');
  const [draftParentId, setDraftParentId] = useState<string>('');
  // Create only: the parent picked with Find (null = no parent). Edit shows
  // the stored parent_id read-only, as before.
  const [draftParent, setDraftParent] = useState<ParentAssetMatch | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftSources, setDraftSources] = useState<DraftSource[]>([
    { source_meter_asset_id: '', operation: '+', fraction: 1.0 },
  ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [vmRes, meterRes] = await Promise.all([
        api.listVirtualMeters(),
        api.getBillableMetersV2(),
      ]);
      setVirtualMeters(vmRes.data || []);
      // Source dropdown is filtered to non-virtual ElectricityConsumerAssets
      // — a virtual meter can't depend on another virtual meter (Phase 1 limit).
      setCandidateSources(
        (meterRes.data || []).filter(
          (m) => m.meter_type === 'ElectricityConsumerAsset' && !m.is_virtual
        )
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load virtual meters');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditorId(null);
    setDraftSerial('');
    setDraftParentId('');
    setDraftParent(null);
    setDraftSources([{ source_meter_asset_id: '', operation: '+', fraction: 1.0 }]);
  };

  const openEdit = (vm: VirtualMeter) => {
    setEditorId(vm.id);
    setDraftSerial(vm.meter_serial || '');
    setDraftParentId(vm.parent_id || '');
    setDraftSources(
      vm.sources.length > 0
        ? vm.sources.map((s) => ({
            source_meter_asset_id: s.source_meter_asset_id,
            operation: s.operation,
            fraction: s.fraction,
          }))
        : [{ source_meter_asset_id: '', operation: '+', fraction: 1.0 }]
    );
  };

  const closeEditor = () => {
    setEditorId(undefined);
  };

  const addSourceRow = () =>
    setDraftSources((prev) => [...prev, { source_meter_asset_id: '', operation: '+', fraction: 1.0 }]);
  const removeSourceRow = (idx: number) =>
    setDraftSources((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const updateSourceRow = (idx: number, patch: Partial<DraftSource>) =>
    setDraftSources((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const validate = (): string | null => {
    if (!draftSerial.trim()) return 'Name is required';
    for (const s of draftSources) {
      if (!s.source_meter_asset_id) return 'Each source must have a meter selected';
      if (!isFinite(s.fraction) || s.fraction <= 0) return 'Each source fraction must be > 0';
    }
    const seen = new Set<string>();
    for (const s of draftSources) {
      const key = `${s.source_meter_asset_id}|${s.operation}`;
      if (seen.has(key)) return 'Duplicate source/operation combination — combine fractions instead';
      seen.add(key);
    }
    return null;
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editorId === null) {
        await api.createVirtualMeter({
          meter_serial: draftSerial.trim(),
          parent_id: draftParent?.asset_id ?? null,
          sources: draftSources.map((s) => ({
            source_meter_asset_id: s.source_meter_asset_id,
            operation: s.operation,
            fraction: s.fraction,
          })),
        });
      } else if (editorId) {
        await api.patchVirtualMeterSources(
          editorId,
          draftSources.map((s) => ({
            source_meter_asset_id: s.source_meter_asset_id,
            operation: s.operation,
            fraction: s.fraction,
          })),
        );
      }
      closeEditor();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vm: VirtualMeter) => {
    if (!confirm(`Delete virtual meter "${vm.meter_serial}"?\n\nThe Portal asset will NOT be auto-deleted — handle that out-of-band if you no longer need the asset hierarchy entry.`)) return;
    try {
      setError(null);
      await api.deleteVirtualMeter(vm.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-content">Virtual Meters</h1>
          <p className="text-sm text-content-muted mt-1">
            Software-defined meter points computed as <code>Σ ±fraction × source</code>.
            The Portal asset is created automatically — assign a tariff via Meter Config like any physical meter.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 min-h-tap lg:min-h-0 inline-flex items-center justify-center bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50"
          disabled={editorId !== undefined}
        >
          + New Virtual Meter
        </button>
      </div>

      {error && (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {pickerOpen && (
        <ParentAssetPicker
          description={
            <p className="text-sm text-content-muted mt-0.5">
              Pick the Building or Group the virtual meter sits under. Drill in with ▸ to go deeper.
            </p>
          }
          realm={api.currentRealm()}
          realmMode="locked"
          pickLabel="Use this parent"
          busy={false}
          onCancel={() => setPickerOpen(false)}
          onPick={(p) => { setDraftParent(p); setPickerOpen(false); }}
        />
      )}

      {editorId !== undefined && (
        <Card className="mb-6 shadow-sm p-5">
          <h2 className="text-lg font-semibold text-content mb-4">
            {editorId === null ? 'Create Virtual Meter' : 'Edit Sources'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">Name (Portal asset name)</label>
              <input
                type="text"
                value={draftSerial}
                onChange={(e) => setDraftSerial(e.target.value)}
                disabled={editorId !== null}
                placeholder="e.g. TEST-VM-01 or Phisantekraal-Net-Demand"
                className="w-full px-3 py-2 min-h-tap lg:min-h-0 border border-border-token rounded-lg disabled:bg-surface-sunken disabled:text-content-muted"
              />
              {editorId !== null && (
                <p className="text-xs text-content-muted mt-1">Name is fixed after creation — delete and recreate to rename.</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">Portal parent (optional)</label>
              {editorId !== null ? (
                <p className="px-3 py-2 border border-border-token rounded-lg font-mono text-xs bg-surface-sunken text-content-muted break-all">
                  {draftParentId || 'None'}
                </p>
              ) : (
                // Find, locked to the realm being worked in: a virtual meter's
                // parent must sit in its own realm (the server enforces it too).
                <div className="flex flex-wrap items-center gap-2">
                  {draftParent ? (
                    <span className="min-w-0 text-sm text-content break-words">
                      {draftParent.name} <span className="text-xs text-content-muted">({draftParent.asset_type})</span>
                    </span>
                  ) : (
                    <span className="text-sm text-content-muted">None</span>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>Find</Button>
                  {draftParent && (
                    <Button size="sm" variant="ghost" onClick={() => setDraftParent(null)}>Clear</Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-content-muted">Sources</label>
              <button
                onClick={addSourceRow}
                className="text-xs px-2 py-1 min-h-tap lg:min-h-0 inline-flex items-center justify-center border border-border-token rounded hover:bg-surface-sunken"
              >
                + Add source
              </button>
            </div>
            <div className="space-y-2">
              {/* Remove-source column is 44px (not 30px) below lg so the "×"
                  button clears the 44px tap-target floor; op-select/fraction/
                  meter-select columns are unchanged. min-w-0 on all three
                  controls stops them from forcing their column past its
                  track width — a bare <select>/<input> in a fixed-width grid
                  cell has an implicit `min-width: auto` (its own min-content
                  size), which can blow the row wider than the card at 375px
                  exactly like the badge/DataTable class of bug this effort
                  keeps running into. */}
              {draftSources.map((s, idx) => (
                <div key={idx} className="grid grid-cols-[60px_100px_1fr_44px] lg:grid-cols-[60px_100px_1fr_30px] gap-2 items-center">
                  <select
                    value={s.operation}
                    onChange={(e) => updateSourceRow(idx, { operation: e.target.value as '+' | '-' })}
                    className="w-full min-w-0 px-2 py-1 min-h-tap lg:min-h-0 border border-border-token rounded text-sm"
                  >
                    <option value="+">+</option>
                    <option value="-">−</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={s.fraction}
                    onChange={(e) => updateSourceRow(idx, { fraction: Number(e.target.value) })}
                    className="w-full min-w-0 px-2 py-1 min-h-tap lg:min-h-0 border border-border-token rounded text-sm"
                  />
                  <select
                    value={s.source_meter_asset_id}
                    onChange={(e) => updateSourceRow(idx, { source_meter_asset_id: e.target.value })}
                    className="w-full min-w-0 px-2 py-1 min-h-tap lg:min-h-0 border border-border-token rounded text-sm"
                  >
                    <option value="">— Select source meter —</option>
                    {candidateSources.map((m) => (
                      <option key={m.meter_asset_id} value={m.meter_asset_id}>
                        {m.meter_name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeSourceRow(idx)}
                    disabled={draftSources.length <= 1}
                    className="inline-flex items-center justify-center min-h-tap min-w-tap lg:min-h-0 lg:min-w-0 text-content-muted hover:text-red-500 dark:hover:text-red-400 disabled:opacity-30 text-lg"
                    title="Remove this source"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-content-muted">
              Cumulative attributes (Import_kWh) and instantaneous attributes (kVA, power) are
              synthesized per 30-min bucket from these sources. TOU bucketing happens after aggregation.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border-token">
            <button
              onClick={closeEditor}
              className="px-3 py-1.5 min-h-tap lg:min-h-0 inline-flex items-center justify-center border border-border-token rounded text-sm hover:bg-surface-sunken"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-1.5 min-h-tap lg:min-h-0 inline-flex items-center justify-center bg-accent text-accent-fg rounded text-sm hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : editorId === null ? 'Create' : 'Save'}
            </button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-center text-content-muted py-12">Loading…</div>
      ) : virtualMeters.length === 0 ? (
        <Card className="p-8 text-center text-content-muted">
          <p>No virtual meters configured yet.</p>
          <p className="text-xs mt-1">Use "+ New Virtual Meter" to create one.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {virtualMeters.map((vm) => (
            <Card key={vm.id} className="p-4">
              <div className="flex items-start justify-between gap-2">
                {/* min-w-0 + truncate: meter_asset_id is an opaque OR asset
                    id with no guaranteed break points (unlike hyphenated
                    UUIDs, browsers won't wrap it), so without this a long id
                    can force the flex row (and the page) wider than the
                    viewport at 375px — the same unbreakable-content overflow
                    risk as a non-wrapping Badge, just from raw text instead. */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-content truncate">{vm.meter_serial || '(unnamed)'}</div>
                  <div className="text-xs text-content-muted font-mono mt-0.5 truncate" title={vm.meter_asset_id}>{vm.meter_asset_id}</div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(vm)}
                    className="text-xs px-3 py-1 min-h-tap lg:min-h-0 inline-flex items-center justify-center border border-border-token rounded hover:bg-surface-sunken"
                  >
                    Edit sources
                  </button>
                  {/* Same treatment as TariffManagement's Delete trigger
                      (wave 2b group A): text-red-700 alone is 2.34:1 against
                      the Card's dark surface (#292524) — fails AA badly.
                      dark:text-red-400 is 5.49:1 — computed by hand, passes. */}
                  <button
                    onClick={() => handleDelete(vm)}
                    className="text-xs px-3 py-1 min-h-tap lg:min-h-0 inline-flex items-center justify-center border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-950"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-1">
                {vm.sources.map((s: VirtualMeterSource) => (
                  <div key={s.id || `${s.source_meter_asset_id}-${s.operation}`} className="flex items-center gap-2 text-sm text-content">
                    {/* text-red-600/text-green-700 are 3.14:1 / 3.02:1
                        against the Card's dark surface — both fail AA.
                        dark:text-red-400 (5.49:1) / dark:text-green-400
                        (8.70:1) computed by hand, pass. */}
                    <span className={`font-mono font-semibold flex-shrink-0 ${s.operation === '-' ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                      {s.operation === '-' ? '−' : '+'} {s.fraction.toFixed(2)} ×
                    </span>
                    {/* Same unbreakable-string overflow guard as the card
                        header above: falls back to the raw source asset id
                        when no name is known. */}
                    <span className="min-w-0 truncate" title={s.source_meter_name || s.source_meter_asset_id}>{s.source_meter_name || s.source_meter_asset_id}</span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
